// Recalibration workspace: configure the ladder, run a calculation, review what came out.
//
// Replaces the old ActualDataPage, which was an actual-spend CRUD form with a single
// "preview" button. Manual entry moves to its own tab and is now one of four actuals
// sources rather than the only one.

import { useState, useEffect, useCallback } from 'react';
import { cmApi } from '../api.js';
import { useClient } from '../ClientContext.jsx';
import SubTabs from '../components/SubTabs.jsx';
import FactorTable from '../components/calibration/FactorTable.jsx';
import OverridesSection from '../components/calibration/OverridesSection.jsx';
import ActualDataPage from './ActualDataPage.jsx';
import { T } from '../../../shared/styles/tokens.js';

const TABS = [
  { id: 'factors',   label: 'Calibration Factors' },
  { id: 'overrides', label: 'Overrides' },
  { id: 'runs',      label: 'Run History' },
  { id: 'actuals',   label: 'Manual Actuals' },
];

const WINDOWS = [
  { value: 3,  label: 'Trailing 3 months' },
  { value: 6,  label: 'Trailing 6 months' },
  { value: 12, label: 'Trailing 12 months' },
  { value: 0,  label: 'All history' },
];

function FactorsTab() {
  const { clientId } = useClient();
  const [config, setConfig] = useState(null);
  const [runId, setRunId] = useState(null);
  const [factors, setFactors] = useState([]);
  const [level, setLevel] = useState('');
  const [band, setBand] = useState('');
  const [windowMonths, setWindowMonths] = useState(3);
  const [enabled, setEnabled] = useState([]);
  const [running, setRunning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const loadConfig = useCallback(() => {
    if (!clientId) return;
    setLoading(true); setErr(null);
    cmApi.getCalibrationConfig(clientId)
      .then(d => {
        setConfig(d);
        setEnabled((d.levels || []).filter(l => l.enabled).map(l => l.level_rank));
        setRunId(d.latest_run?.id || null);
      })
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, [clientId]);

  useEffect(loadConfig, [loadConfig]);

  useEffect(() => {
    if (!clientId || !runId) { setFactors([]); return; }
    cmApi.getCalibrationFactors(clientId, runId, {
      level_rank: level || undefined, band: band || undefined,
    })
      .then(d => setFactors(d.factors || []))
      .catch(e => setErr(e.message));
  }, [clientId, runId, level, band]);

  const run = async () => {
    setRunning(true); setErr(null);
    try {
      const d = await cmApi.runCalibration(clientId, {
        window_months: windowMonths,
        level_ranks: enabled,
      });
      setRunId(d.run.id);
      loadConfig();
    } catch (e) { setErr(e.message); } finally { setRunning(false); }
  };

  if (!clientId) return <p className="o-state">Select a client to calculate factors.</p>;
  if (loading) return <p className="o-state">Loading…</p>;

  const levels = config?.levels || [];
  const latest = config?.latest_run;

  return (
    <div>
      <div className="o-panel" style={{ padding: `${T.s4}px ${T.s5}px`, marginBottom: T.s5 }}>
        <p className="o-eyebrow" style={{ marginBottom: T.s3 }}>Calculate</p>

        <div className="o-ctrl-row" style={{ padding: 0, border: 0 }}>
          <div className="o-ctrl">
            <label className="o-field-lbl">Time window</label>
            <select className="o-control" value={windowMonths}
              onChange={e => setWindowMonths(Number(e.target.value))}>
              {WINDOWS.map(w => <option key={w.value} value={w.value}>{w.label}</option>)}
            </select>
          </div>

          <div className="o-ctrl" style={{ flex: 1 }}>
            <label className="o-field-lbl">Dimension levels, finest first</label>
            <div className="o-chipset">
              {levels.map(l => {
                const on = enabled.includes(l.level_rank);
                return (
                  <button key={l.level_rank} type="button" className="o-chip o-wide"
                    aria-pressed={on}
                    onClick={() => setEnabled(v => on
                      ? v.filter(r => r !== l.level_rank)
                      : [...v, l.level_rank].sort((a, b) => a - b))}>
                    {l.name}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <p className="o-note" style={{ margin: `${T.s3}px 0 ${T.s4}px` }}>
          A group with too few matched months, or with ratios too spread out to trust, widens
          its window before any detail is given up — 3 to 6 to 12 to all history. Only when
          every window fails does a record fall through to a coarser level. Each factor
          records which window and which level produced it.
        </p>

        <div style={{ display: 'flex', gap: T.s3, alignItems: 'center' }}>
          <button className="o-btn o-btn-primary" onClick={run} disabled={running || !enabled.length}>
            {running ? 'Calculating…' : 'Run calculation'}
          </button>
          {latest && (
            <span style={{ fontSize: T.tSmall, color: T.text3 }}>
              Last run #{latest.id} · {String(latest.created_at).slice(0, 16).replace('T', ' ')} ·{' '}
              {latest.factor_count} factor{latest.factor_count === 1 ? '' : 's'}
            </span>
          )}
        </div>
      </div>

      {err && <div className="o-state o-error" style={{ textAlign: 'left' }}>{err}</div>}

      {!runId && !err && (
        <p className="o-hint">
          <b>No calculation has been run yet.</b> Pick a window and the levels you want, then
          run one. Nothing is overwritten — every run is kept so factors can be compared.
        </p>
      )}

      {runId && (
        <>
          <div style={{ display: 'flex', gap: T.s3, marginBottom: T.s4, flexWrap: 'wrap' }}>
            <select className="o-control" style={{ width: 'auto' }}
              value={level} onChange={e => setLevel(e.target.value)}>
              <option value="">All levels</option>
              {levels.map(l => (
                <option key={l.level_rank} value={l.level_rank}>L{l.level_rank} · {l.name}</option>
              ))}
            </select>
            <select className="o-control" style={{ width: 'auto' }}
              value={band} onChange={e => setBand(e.target.value)}>
              <option value="">Any confidence</option>
              <option value="high">High only</option>
              <option value="medium">Medium only</option>
              <option value="low">Low only</option>
            </select>
          </div>
          <FactorTable factors={factors} />
        </>
      )}
    </div>
  );
}

function RunsTab() {
  const { clientId } = useClient();
  const [runs, setRuns] = useState([]);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!clientId) return;
    cmApi.getCalibrationRuns(clientId)
      .then(d => setRuns(d.runs || []))
      .catch(e => setErr(e.message));
  }, [clientId]);

  if (!clientId) return <p className="o-state">Select a client.</p>;
  if (err) return <p className="o-state o-error">{err}</p>;

  return (
    <div>
      <p className="o-note">
        Every calculation is kept. Factors are never updated in place, so a figure produced
        months ago can still be explained by the run that produced it.
      </p>
      <div className="o-panel" style={{ overflowX: 'auto' }}>
        <table className="o-tbl">
          <thead>
            <tr>
              <th>Run</th><th>When</th><th>By</th><th>Window</th>
              <th>Levels</th><th>Factors</th><th>Months</th><th>Status</th>
            </tr>
          </thead>
          <tbody>
            {runs.length === 0 && (
              <tr><td colSpan={8} style={{ textAlign: 'center', color: T.text3 }}>No runs yet.</td></tr>
            )}
            {runs.map(r => (
              <tr key={r.id}>
                <td className="o-mono o-strong">#{r.id}</td>
                <td>{String(r.created_at).slice(0, 16).replace('T', ' ')}</td>
                <td style={{ color: T.text3 }}>{r.created_by || '—'}</td>
                <td>{r.window_months === 0 ? 'All history' : `${r.window_months} months`}</td>
                <td className="o-mono">{(r.level_ranks || []).join(', ')}</td>
                <td className="o-num">{r.factor_count}</td>
                <td className="o-num">{r.matched_months}</td>
                <td>
                  <span className={`o-badge ${r.status === 'complete' ? 'o-ok' : r.status === 'failed' ? 'o-err' : 'o-grey'}`}>
                    {r.status}
                  </span>
                  {r.error && <div style={{ fontSize: 11, color: T.error }}>{r.error}</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function RecalibrationPage() {
  const [tab, setTab] = useState('factors');
  return (
    <div style={{ padding: 20 }}>
      <SubTabs tabs={TABS} active={tab} onChange={setTab} />
      {tab === 'factors' ? <FactorsTab />
        : tab === 'overrides' ? <OverridesSection />
        : tab === 'runs' ? <RunsTab />
        : <ActualDataPage />}
    </div>
  );
}
